import type { ReactNode } from "react";

import { FullPageLoading } from "components/states";

import { SignInScreen } from "./SignInScreen";
import { useAuthUser } from "./useAuthUser";
import { useSignOutToLanding } from "./use-sign-out";

/**
 * The auth gate. Wrap the routes that need a signed-in user.
 *
 * It renders the sign-in screen rather than redirecting, so a deep link
 * survives the sign-in: the user lands where they were going instead of on a
 * dashboard. A deliberate sign-out is the exception: it goes to "/" first
 * (useSignOutToLanding), so the next sign-in does not inherit the page the
 * last person left. `children` is a function so a gated page receives the user without
 * every page re-reading auth state.
 *
 * This is a CLIENT-side gate: it decides what to render, never what may be
 * read. Authorization lives in firestore.rules and storage.rules — a gate in the
 * UI is convenience, and treating it as the boundary is how a token ends up
 * reading another tenant's data.
 */
export function RequireAuth({
  appName,
  tagline,
  children,
}: {
  appName?: string;
  tagline?: string;
  children: (user: {
    uid: string;
    email: string | null;
    /** Pass to AppShell's onSignOut: the gate owns the auth actions, the shell
     *  only renders them. */
    signOut: () => void;
  }) => ReactNode;
}) {
  // Profile provisioning is NOT here. It moved to SessionBoundary at the router
  // root, because sign-in no longer only happens behind this gate: /e/$slug
  // signs an invitee in without ever mounting RequireAuth.
  const { user, loading } = useAuthUser();
  const signOut = useSignOutToLanding();

  if (loading) return <FullPageLoading />;

  if (!user) {
    return <SignInScreen appName={appName} tagline={tagline} />;
  }

  return (
    <>
      {children({
        uid: user.uid,
        email: user.email,
        signOut: () => signOut(user.uid),
      })}
    </>
  );
}
